import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plug, Save } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent, type JSX, type ReactNode } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Select } from '@/components/ui/Select';
import { ApiError } from '@/lib/api';
import { checkNit } from '@/lib/tenant-identity';

import { ConfigSection } from './ConfigSection';
import factusStyles from './TenantFactusTab.module.scss';
import {
  getTenantFactusConfig,
  setTenantFactusStatus,
  setTenantPayrollElectronicStatus,
  testTenantFactusConnection,
  upsertTenantFactusConfig,
  upsertTenantPayrollElectronicConfig,
  type FactusConfig,
  type FactusConnectionTestResult,
  type FactusEnvironment,
  type FactusStatus,
  type UpsertFactusConfigInput,
  type UpsertPayrollElectronicInput,
} from '../api/factus.graphql';
import styles from '../pages/EditTenantPage.module.scss';

export function tenantFactusQueryKey(tenantId: string): readonly unknown[] {
  return ['platform-tenants', 'factus', tenantId];
}

export const FACTUS_PRODUCTION_WARNING =
  'Los documentos en producción tienen validez fiscal ante la DIAN.';

const ENVIRONMENT_LABEL: Record<FactusEnvironment, string> = {
  SANDBOX: 'Sandbox (pruebas)',
  PRODUCTION: 'Producción',
};
const STATUS_LABEL: Record<FactusStatus, string> = {
  PENDING: 'Pendiente',
  ACTIVE: 'Activa',
  SUSPENDED: 'Suspendida',
};
const STATUS_CLASS: Record<FactusStatus, string> = {
  PENDING: factusStyles.pending ?? '',
  ACTIVE: factusStyles.active ?? '',
  SUSPENDED: factusStyles.suspended ?? '',
};

const MUNICIPALITY = /^\d{5}$/;
const PREFIX = /^[A-Za-z0-9]{1,10}$/;
const RANGE_ID = /^\d{1,10}$/;

export interface FactusForm {
  environment: FactusEnvironment;
  clientId: string;
  factusUsername: string;
  /** Write-only: nunca se precarga. Solo se envía si el admin lo escribe. */
  clientSecret: string;
  factusPassword: string;
  nit: string;
  companyName: string;
  municipalityCode: string;
  economicActivity: string;
  invoicePrefix: string;
  numberingRangeId: string;
  defaultTaxRate: string;
  dianResolutionNumber: string;
  dianResolutionIssuedOn: string;
  dianRangeFrom: string;
  dianRangeTo: string;
}
type FactusErrors = Partial<Record<keyof FactusForm, string>>;

export function emptyFactusForm(): FactusForm {
  return {
    environment: 'SANDBOX',
    clientId: '',
    factusUsername: '',
    clientSecret: '',
    factusPassword: '',
    nit: '',
    companyName: '',
    municipalityCode: '',
    economicActivity: '',
    invoicePrefix: '',
    numberingRangeId: '',
    defaultTaxRate: '19',
    dianResolutionNumber: '',
    dianResolutionIssuedOn: '',
    dianRangeFrom: '',
    dianRangeTo: '',
  };
}

/** Los secretos del formulario SIEMPRE arrancan vacíos: el API no los devuelve. */
export function formFromConfig(config: FactusConfig): FactusForm {
  return {
    environment: config.environment,
    clientId: config.clientId,
    factusUsername: config.factusUsername,
    clientSecret: '',
    factusPassword: '',
    nit: `${config.nit}-${config.dv}`,
    companyName: config.companyName,
    municipalityCode: config.municipalityCode,
    economicActivity: config.economicActivity ?? '',
    invoicePrefix: config.invoicePrefix ?? '',
    numberingRangeId: config.numberingRangeId === null ? '' : String(config.numberingRangeId),
    defaultTaxRate: String(config.defaultTaxRate),
    dianResolutionNumber: config.dianResolutionNumber ?? '',
    dianResolutionIssuedOn: config.dianResolutionIssuedOn ?? '',
    dianRangeFrom: config.dianRangeFrom === null ? '' : String(config.dianRangeFrom),
    dianRangeTo: config.dianRangeTo === null ? '' : String(config.dianRangeTo),
  };
}

function optionalInt(value: string): number | null {
  return value.trim() ? Number(value) : null;
}

interface SecretState {
  /** Ya hay un secreto guardado (el API solo dice si existe). */
  stored: boolean;
  /** El admin pulsó "Cambiar": se espera un valor nuevo. */
  changing: boolean;
}

/**
 * Valida en la consola lo mismo que el API (que sigue siendo la fuente de verdad). Un secreto solo
 * entra en `input` si el admin lo escribió: ausente = el API conserva el guardado; jamás se envía
 * vacío.
 */
export function buildFactusInput(
  form: FactusForm,
  secrets: { clientSecret: SecretState; factusPassword: SecretState },
): { input: UpsertFactusConfigInput | null; errors: FactusErrors } {
  const errors: FactusErrors = {};
  const required = (key: 'clientId' | 'factusUsername' | 'companyName', message: string): void => {
    if (!form[key].trim()) {errors[key] = message;}
  };
  required('clientId', 'El client ID es obligatorio.');
  required('factusUsername', 'El usuario de Factus es obligatorio.');
  required('companyName', 'La razón social es obligatoria.');

  for (const key of ['clientSecret', 'factusPassword'] as const) {
    const needsValue = !secrets[key].stored || secrets[key].changing;
    if (needsValue && !form[key]) {
      errors[key] = secrets[key].stored
        ? 'Escribe el valor nuevo o cancela el cambio.'
        : 'Este secreto es obligatorio.';
    }
  }

  const nit = checkNit(form.nit);
  if (!form.nit.trim()) {errors.nit = 'El NIT es obligatorio.';}
  else if (!nit.ok) {errors.nit = nit.message;}

  if (!MUNICIPALITY.test(form.municipalityCode.trim())) {
    errors.municipalityCode = 'Usa el código DIVIPOLA de 5 dígitos, p. ej. 11001.';
  }
  const rate = Number(form.defaultTaxRate);
  if (!form.defaultTaxRate.trim() || !Number.isFinite(rate) || rate < 0 || rate > 100) {
    errors.defaultTaxRate = 'La tasa debe estar entre 0 y 100.';
  }
  if (form.invoicePrefix.trim() && !PREFIX.test(form.invoicePrefix.trim())) {
    errors.invoicePrefix = 'Alfanumérico, de 1 a 10 caracteres.';
  }
  for (const key of ['numberingRangeId', 'dianRangeFrom', 'dianRangeTo'] as const) {
    const value = form[key].trim();
    if (value && !(RANGE_ID.test(value) && Number(value) >= 1)) {
      errors[key] = 'Debe ser un entero positivo.';
    }
  }
  const from = optionalInt(form.dianRangeFrom);
  const to = optionalInt(form.dianRangeTo);
  if (!errors.dianRangeFrom && !errors.dianRangeTo && from !== null && to !== null && from > to) {
    errors.dianRangeTo = 'El rango es incoherente: "desde" no puede superar a "hasta".';
  }

  if (Object.keys(errors).length > 0 || !nit.ok) {
    return { input: null, errors };
  }
  const optional = (value: string): string | null => value.trim() || null;
  return {
    input: {
      environment: form.environment,
      clientId: form.clientId.trim(),
      factusUsername: form.factusUsername.trim(),
      ...(form.clientSecret ? { clientSecret: form.clientSecret } : {}),
      ...(form.factusPassword ? { factusPassword: form.factusPassword } : {}),
      nit: nit.nit,
      verificationDigit: nit.verificationDigit,
      companyName: form.companyName.trim(),
      municipalityCode: form.municipalityCode.trim(),
      economicActivity: optional(form.economicActivity),
      invoicePrefix: optional(form.invoicePrefix),
      numberingRangeId: optionalInt(form.numberingRangeId),
      defaultTaxRate: rate,
      dianResolutionNumber: optional(form.dianResolutionNumber),
      dianResolutionIssuedOn: optional(form.dianResolutionIssuedOn),
      dianRangeFrom: from,
      dianRangeTo: to,
    },
    errors,
  };
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('es-CO', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/Bogota',
  });
}

function connectionSummary(config: FactusConfig): string {
  if (!config.lastConnectionTestAt) {return 'Sin probar';}
  const when = formatDateTime(config.lastConnectionTestAt);
  return config.lastConnectionTestOk ? `Exitosa · ${when}` : `Fallida · ${when}`;
}

function StatusChip({ status }: { status: FactusStatus }): JSX.Element {
  return <span className={`${factusStyles.chip} ${STATUS_CLASS[status]}`}>{STATUS_LABEL[status]}</span>;
}

function EnvironmentChip({ environment }: { environment: FactusEnvironment }): JSX.Element {
  return (
    <span
      className={`${factusStyles.chip} ${environment === 'PRODUCTION' ? factusStyles.production : factusStyles.sandbox}`}
    >
      {environment === 'PRODUCTION' ? 'Producción' : 'Sandbox'}
    </span>
  );
}

interface Props {
  tenantId: string;
  /** `platform.billing.factus.write`; sin ella la pestaña es de solo lectura. */
  canWrite: boolean;
}

type StatusTarget = { scope: 'factus' | 'payroll'; status: FactusStatus };

/**
 * Admin Console "Factus por tenant" — credenciales, datos fiscales y estado de la facturación
 * electrónica del tenant, y su nómina electrónica (misma cuenta Factus). Solo plataforma: el
 * restaurante nunca ve estas credenciales. Los secretos son write-only: jamás se precargan ni se
 * envían vacíos, y la consola nunca los registra.
 */
export function TenantFactusTab({ tenantId, canWrite }: Props): JSX.Element {
  const qc = useQueryClient();
  const idPrefix = useId();
  const queryKey = tenantFactusQueryKey(tenantId);
  const query = useQuery({ queryKey, queryFn: () => getTenantFactusConfig(tenantId) });
  const config = query.data ?? null;

  const [form, setForm] = useState<FactusForm | null>(null);
  const [baseline, setBaseline] = useState<FactusForm | null>(null);
  const [changing, setChanging] = useState({ clientSecret: false, factusPassword: false });
  const [errors, setErrors] = useState<FactusErrors>({});
  const [saved, setSaved] = useState(false);
  const [pendingInput, setPendingInput] = useState<UpsertFactusConfigInput | null>(null);
  const [statusTarget, setStatusTarget] = useState<StatusTarget | null>(null);
  const [testResult, setTestResult] = useState<FactusConnectionTestResult | null>(null);

  useEffect(() => {
    if (query.isSuccess && form === null) {
      const initial = query.data ? formFromConfig(query.data) : emptyFactusForm();
      setForm(initial);
      setBaseline(initial);
    }
  }, [query.isSuccess, query.data, form]);

  function applyConfig(next: FactusConfig): void {
    qc.setQueryData(queryKey, next);
    const initial = formFromConfig(next);
    setForm(initial);
    setBaseline(initial);
    setChanging({ clientSecret: false, factusPassword: false });
    setErrors({});
  }

  const saveMutation = useMutation({
    mutationFn: (input: UpsertFactusConfigInput) => upsertTenantFactusConfig(tenantId, input),
    onSuccess: (next) => {
      applyConfig(next);
      setTestResult(null);
      setSaved(true);
      setPendingInput(null);
    },
    onError: (err) => {
      setPendingInput(null);
      if (err instanceof ApiError && err.field) {
        const key = (err.field === 'verificationDigit' ? 'nit' : err.field) as keyof FactusForm;
        setErrors({ [key]: err.message });
        document.getElementById(`${idPrefix}-${key}`)?.focus();
      }
    },
  });

  const testMutation = useMutation({
    mutationFn: () => testTenantFactusConnection(tenantId),
    onSuccess: (result) => {
      setTestResult(result);
      void qc.invalidateQueries({ queryKey });
    },
  });

  const statusMutation = useMutation({
    mutationFn: (target: StatusTarget) =>
      target.scope === 'factus'
        ? setTenantFactusStatus(tenantId, target.status)
        : setTenantPayrollElectronicStatus(tenantId, target.status),
    onSuccess: (next) => {
      qc.setQueryData(queryKey, next);
      setStatusTarget(null);
    },
  });

  if (query.isLoading) {return <p className={styles.loading} role="status">Cargando…</p>;}
  if (query.isError) {
    return (
      <p className={styles.error} role="alert">
        No se pudo cargar la configuración de Factus: {query.error.message}
      </p>
    );
  }
  if (!form || !baseline) {return <p className={styles.loading} role="status">Cargando…</p>;}

  const dirty = JSON.stringify(form) !== JSON.stringify(baseline) || changing.clientSecret || changing.factusPassword;
  const secrets = {
    clientSecret: { stored: Boolean(config?.hasClientSecret), changing: changing.clientSecret },
    factusPassword: { stored: Boolean(config?.hasPassword), changing: changing.factusPassword },
  };

  function update<K extends keyof FactusForm>(key: K, value: FactusForm[K]): void {
    setSaved(false);
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setErrors((prev) => {
      if (!(key in prev)) {return prev;}
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    if (!form || !baseline) {return;}
    const { input, errors: found } = buildFactusInput(form, secrets);
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      document.getElementById(`${idPrefix}-${first}`)?.focus();
      return;
    }
    if (!input) {return;}
    // Pasar a producción tiene validez fiscal: se confirma antes de guardar.
    if (input.environment === 'PRODUCTION' && config?.environment !== 'PRODUCTION') {
      setPendingInput(input);
      return;
    }
    saveMutation.mutate(input);
  }

  const fieldProps = (
    key: keyof FactusForm,
  ): { id: string; 'aria-invalid': boolean; 'aria-describedby': string | undefined } => ({
    id: `${idPrefix}-${key}`,
    'aria-invalid': Boolean(errors[key]),
    'aria-describedby': errors[key] ? `${idPrefix}-${key}-error` : undefined,
  });
  const fieldError = (key: keyof FactusForm): ReactNode =>
    errors[key] ? (
      <span id={`${idPrefix}-${key}-error`} className={styles.fieldError} role="alert">
        {errors[key]}
      </span>
    ) : null;

  const textField = (
    key: keyof FactusForm,
    label: string,
    options: { placeholder?: string; type?: string; hint?: string; inputMode?: 'numeric' | 'decimal' } = {},
  ): JSX.Element => (
    <label className={styles.field}>
      {label}
      <input
        type={options.type ?? 'text'}
        inputMode={options.inputMode}
        autoComplete="off"
        {...fieldProps(key)}
        value={form[key]}
        placeholder={options.placeholder}
        onChange={(e) => update(key, e.target.value)}
      />
      {fieldError(key)}
      {options.hint ? <span className={styles.fieldHint}>{options.hint}</span> : null}
    </label>
  );

  /** Secreto write-only: "Configurado ••••" + Cambiar; vacío y obligatorio si no existe. */
  const secretField = (key: 'clientSecret' | 'factusPassword', label: string): JSX.Element => {
    const state = secrets[key];
    if (state.stored && !state.changing) {
      return (
        <div className={styles.field}>
          <span id={`${idPrefix}-${key}-label`}>{label}</span>
          <div className={factusStyles.secretRow} aria-labelledby={`${idPrefix}-${key}-label`}>
            <span>Configurado ••••</span>
            <button
              type="button"
              className={factusStyles.secondaryBtn}
              aria-label={`Cambiar ${label.toLowerCase()}`}
              onClick={() => setChanging((prev) => ({ ...prev, [key]: true }))}
            >
              Cambiar
            </button>
          </div>
        </div>
      );
    }
    return (
      <label className={styles.field}>
        {label}
        {state.stored ? '' : ' *'}
        <input
          type="password"
          autoComplete="new-password"
          {...fieldProps(key)}
          value={form[key]}
          onChange={(e) => update(key, e.target.value)}
        />
        {fieldError(key)}
        {state.stored ? (
          <button
            type="button"
            className={factusStyles.secondaryBtn}
            onClick={() => {
              setChanging((prev) => ({ ...prev, [key]: false }));
              update(key, '');
            }}
          >
            Cancelar cambio
          </button>
        ) : (
          <span className={styles.fieldHint}>Se guarda cifrado y no se vuelve a mostrar.</span>
        )}
      </label>
    );
  };

  const factusReady = config !== null;
  const statusButtons = (scope: 'factus' | 'payroll', current: FactusStatus, disabled: boolean): ReactNode =>
    canWrite ? (
      <div className={factusStyles.actions}>
        {current !== 'ACTIVE' ? (
          <button
            type="button"
            className={factusStyles.secondaryBtn}
            disabled={disabled}
            onClick={() => setStatusTarget({ scope, status: 'ACTIVE' })}
          >
            {scope === 'factus' ? 'Activar facturación electrónica' : 'Activar nómina electrónica'}
          </button>
        ) : (
          <button
            type="button"
            className={factusStyles.dangerBtn}
            disabled={disabled}
            onClick={() => setStatusTarget({ scope, status: 'SUSPENDED' })}
          >
            {scope === 'factus' ? 'Suspender facturación electrónica' : 'Suspender nómina electrónica'}
          </button>
        )}
      </div>
    ) : null;

  const statusDialog = statusTarget ? (
    <ConfirmDialog
      title={
        statusTarget.status === 'ACTIVE'
          ? `Activar ${statusTarget.scope === 'factus' ? 'facturación electrónica' : 'nómina electrónica'}`
          : `Suspender ${statusTarget.scope === 'factus' ? 'facturación electrónica' : 'nómina electrónica'}`
      }
      confirmLabel={statusTarget.status === 'ACTIVE' ? 'Activar' : 'Suspender'}
      danger={statusTarget.status !== 'ACTIVE'}
      pending={statusMutation.isPending}
      error={statusMutation.error ? statusMutation.error.message : null}
      onConfirm={() => statusMutation.mutate(statusTarget)}
      onCancel={() => {
        statusMutation.reset();
        setStatusTarget(null);
      }}
    >
      {statusTarget.status === 'ACTIVE' ? (
        <>
          <p>
            {statusTarget.scope === 'factus'
              ? 'Se probará la conexión con Factus antes de activar si no hay una prueba exitosa reciente.'
              : 'Nómina electrónica usa las mismas credenciales de Factus y debe estar en el mismo ambiente.'}
          </p>
          {(statusTarget.scope === 'factus' ? config?.environment : config?.payrollElectronic?.environment) ===
          'PRODUCTION' ? (
            <p role="note">{FACTUS_PRODUCTION_WARNING}</p>
          ) : null}
        </>
      ) : (
        <p>
          {statusTarget.scope === 'factus'
            ? 'El tenant dejará de emitir facturación electrónica y su nómina electrónica también se suspende. Podrás reactivarla después.'
            : 'El tenant dejará de transmitir nómina electrónica. Podrás reactivarla después.'}
        </p>
      )}
    </ConfirmDialog>
  ) : null;

  // ---- Solo lectura ----
  if (!canWrite) {
    if (!config) {
      return (
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Facturación electrónica (Factus)</h2>
          <p className={styles.fieldHint}>Solo lectura: este tenant aún no tiene configuración de Factus.</p>
        </section>
      );
    }
    const rows: [string, string][] = [
      ['Client ID', config.clientId],
      ['Usuario Factus', config.factusUsername],
      ['Client secret', config.hasClientSecret ? 'Configurado ••••' : 'No configurado'],
      ['Contraseña', config.hasPassword ? 'Configurado ••••' : 'No configurado'],
      ['NIT', `${config.nit}-${config.dv}`],
      ['Razón social', config.companyName],
      ['Municipio (DIVIPOLA)', config.municipalityCode],
      ['Actividad económica', config.economicActivity ?? '—'],
      ['Prefijo', config.invoicePrefix ?? '—'],
      ['Rango de numeración', config.numberingRangeId === null ? '—' : String(config.numberingRangeId)],
      ['Tasa por defecto', `${config.defaultTaxRate} %`],
      ['Resolución DIAN', config.dianResolutionNumber ?? '—'],
      ['Fecha de resolución', config.dianResolutionIssuedOn ?? '—'],
      ['Rango DIAN', config.dianRangeFrom !== null && config.dianRangeTo !== null ? `${config.dianRangeFrom} – ${config.dianRangeTo}` : '—'],
    ];
    return (
      <section className={styles.panel} aria-labelledby={`${idPrefix}-title`}>
        <h2 id={`${idPrefix}-title`} className={styles.panelTitle}>
          Facturación electrónica (Factus)
        </h2>
        <p className={styles.fieldHint}>Solo lectura: tu rol no edita la configuración de Factus.</p>
        <div className={factusStyles.facts}>
          <StatusChip status={config.status} />
          <EnvironmentChip environment={config.environment} />
          <span>Última prueba de conexión: {connectionSummary(config)}</span>
        </div>
        <dl className={styles.grid}>
          {rows.map(([term, value]) => (
            <div key={term} className={styles.field}>
              <dt>{term}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
        <h3 className={styles.panelTitle}>Nómina electrónica</h3>
        {config.payrollElectronic ? (
          <div className={factusStyles.facts}>
            <StatusChip status={config.payrollElectronic.status} />
            <EnvironmentChip environment={config.payrollElectronic.environment} />
            <span>Rango: {config.payrollElectronic.numberingRangeId ?? '—'}</span>
            <span>Prefijo: {config.payrollElectronic.prefix ?? '—'}</span>
          </div>
        ) : (
          <p className={styles.fieldHint}>Sin configurar.</p>
        )}
      </section>
    );
  }

  // ---- Edición ----
  const saveError =
    saveMutation.error && !(saveMutation.error instanceof ApiError && saveMutation.error.field)
      ? saveMutation.error.message
      : null;

  return (
    <div className={styles.panel}>
      <h2 className={styles.panelTitle}>Facturación electrónica (Factus)</h2>
      <p className={styles.fieldHint}>
        Solo visible para plataforma: el restaurante no ve estas credenciales. Los secretos se guardan
        cifrados y no se vuelven a mostrar.
      </p>

      <ConfigSection title="Estado" description="Estado de la facturación electrónica del tenant.">
        {config ? (
          <>
            <div className={factusStyles.facts}>
              <StatusChip status={config.status} />
              <EnvironmentChip environment={config.environment} />
              <span>Última prueba de conexión: {connectionSummary(config)}</span>
              <span>{config.hasValidToken ? 'Token vigente' : 'Sin token vigente'}</span>
            </div>
            {statusButtons('factus', config.status, dirty || statusMutation.isPending)}
            {dirty ? (
              <p className={styles.fieldHint}>Guarda los cambios antes de activar o suspender.</p>
            ) : null}
          </>
        ) : (
          <p className={styles.fieldHint}>
            Este tenant aún no tiene configuración. Completa y guarda el formulario; quedará en estado
            Pendiente.
          </p>
        )}
      </ConfigSection>

      <form onSubmit={handleSubmit} noValidate className={styles.panel} aria-label="Configuración de Factus">
        <ConfigSection title="Credenciales" description="Cuenta Factus del restaurante (OAuth).">
          <div className={styles.grid}>
            <label className={styles.field}>
              Ambiente
              <Select
                {...fieldProps('environment')}
                value={form.environment}
                onChange={(value) => update('environment', value)}
                options={(Object.keys(ENVIRONMENT_LABEL) as FactusEnvironment[]).map((value) => ({
                  value,
                  label: ENVIRONMENT_LABEL[value],
                }))}
              />
            </label>
            <span />
            {textField('clientId', 'Client ID *')}
            {textField('factusUsername', 'Usuario de Factus *', { type: 'text' })}
            {secretField('clientSecret', 'Client secret')}
            {secretField('factusPassword', 'Contraseña')}
          </div>
          {form.environment === 'PRODUCTION' ? (
            <p className={factusStyles.warning} role="note">
              {FACTUS_PRODUCTION_WARNING} Cambiar de ambiente invalida los tokens y devuelve la
              configuración a Pendiente.
            </p>
          ) : null}
          <div className={factusStyles.actions}>
            <button
              type="button"
              className={factusStyles.secondaryBtn}
              disabled={!factusReady || dirty || testMutation.isPending}
              onClick={() => testMutation.mutate()}
            >
              <Plug size={14} aria-hidden="true" />{' '}
              {testMutation.isPending ? 'Probando…' : 'Probar conexión'}
            </button>
            {!factusReady || dirty ? (
              <span className={styles.fieldHint}>Guarda la configuración para probar la conexión.</span>
            ) : null}
          </div>
          {testResult ? (
            <p className={testResult.ok ? factusStyles.testOk : factusStyles.testFail} role="status">
              {testResult.message}
            </p>
          ) : null}
          {testMutation.error ? (
            <p className={styles.error} role="alert">
              {testMutation.error.message}
            </p>
          ) : null}
        </ConfigSection>

        <ConfigSection title="Datos fiscales" description="Emisor que sale en los documentos electrónicos.">
          <div className={styles.grid}>
            {textField('nit', 'NIT (con dígito de verificación) *', {
              placeholder: '900123456-8',
              inputMode: 'numeric',
            })}
            {textField('companyName', 'Razón social *')}
            {textField('municipalityCode', 'Municipio (código DIVIPOLA) *', {
              placeholder: '11001',
              inputMode: 'numeric',
            })}
            {textField('economicActivity', 'Actividad económica', { placeholder: '5611 - 4723' })}
            {textField('invoicePrefix', 'Prefijo de facturación')}
            {textField('numberingRangeId', 'Rango de numeración (Factus)', { inputMode: 'numeric' })}
            {textField('defaultTaxRate', 'Tasa por defecto (%) *', { inputMode: 'decimal' })}
          </div>
          <h4 className={styles.panelTitle}>Resolución DIAN</h4>
          <div className={styles.grid}>
            {textField('dianResolutionNumber', 'Número de resolución')}
            {textField('dianResolutionIssuedOn', 'Fecha de resolución', { type: 'date' })}
            {textField('dianRangeFrom', 'Rango desde', { inputMode: 'numeric' })}
            {textField('dianRangeTo', 'Rango hasta', { inputMode: 'numeric' })}
          </div>
        </ConfigSection>

        {saveError ? (
          <p className={styles.error} role="alert">
            {saveError}
          </p>
        ) : null}
        <div>
          <button type="submit" className={styles.saveBtn} disabled={saveMutation.isPending || !dirty}>
            <Save size={16} aria-hidden="true" />
            {saveMutation.isPending ? 'Guardando…' : 'Guardar configuración'}
          </button>
        </div>
        <p className={styles.success} role="status">
          {saved ? 'Configuración de Factus guardada.' : ''}
        </p>
      </form>

      {config ? (
        <PayrollElectronicBlock
          tenantId={tenantId}
          config={config}
          onSaved={(next) => qc.setQueryData(queryKey, next)}
          statusButtons={statusButtons}
        />
      ) : (
        <ConfigSection title="Nómina electrónica">
          <p className={styles.fieldHint}>Guarda primero la configuración de Factus.</p>
        </ConfigSection>
      )}

      {pendingInput ? (
        <ConfirmDialog
          title="Cambiar a producción"
          confirmLabel="Guardar en producción"
          danger
          pending={saveMutation.isPending}
          error={saveMutation.error ? saveMutation.error.message : null}
          onConfirm={() => saveMutation.mutate(pendingInput)}
          onCancel={() => {
            saveMutation.reset();
            setPendingInput(null);
          }}
        >
          <p>{FACTUS_PRODUCTION_WARNING}</p>
          <p>La configuración volverá a Pendiente y habrá que probar la conexión y activarla de nuevo.</p>
        </ConfirmDialog>
      ) : null}
      {statusDialog}
    </div>
  );
}

interface PayrollProps {
  tenantId: string;
  config: FactusConfig;
  onSaved: (next: FactusConfig) => void;
  statusButtons: (scope: 'factus' | 'payroll', current: FactusStatus, disabled: boolean) => ReactNode;
}

interface PayrollForm {
  environment: FactusEnvironment;
  numberingRangeId: string;
  prefix: string;
}

function payrollFormFrom(config: FactusConfig): PayrollForm {
  const payroll = config.payrollElectronic;
  return {
    environment: payroll?.environment ?? config.environment,
    numberingRangeId: payroll?.numberingRangeId ?? '',
    prefix: payroll?.prefix ?? '',
  };
}

/** Nómina electrónica: mismas credenciales de Factus; ambiente, rango y prefijo propios. */
function PayrollElectronicBlock({ tenantId, config, onSaved, statusButtons }: PayrollProps): JSX.Element {
  const idPrefix = useId();
  const payroll = config.payrollElectronic;
  const [form, setForm] = useState<PayrollForm>(() => payrollFormFrom(config));
  const [errors, setErrors] = useState<Partial<Record<keyof PayrollForm, string>>>({});
  const [confirm, setConfirm] = useState<UpsertPayrollElectronicInput | null>(null);
  const [saved, setSaved] = useState(false);

  const baseline = payrollFormFrom(config);
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline) || payroll === null;

  const mutation = useMutation({
    mutationFn: (input: UpsertPayrollElectronicInput) => upsertTenantPayrollElectronicConfig(tenantId, input),
    onSuccess: (next) => {
      onSaved(next);
      setForm(payrollFormFrom(next));
      setConfirm(null);
      setSaved(true);
    },
    onError: (err) => {
      setConfirm(null);
      if (err instanceof ApiError && err.field && err.field in form) {
        setErrors({ [err.field]: err.message });
      }
    },
  });

  function update<K extends keyof PayrollForm>(key: K, value: PayrollForm[K]): void {
    setSaved(false);
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    const found: Partial<Record<keyof PayrollForm, string>> = {};
    if (form.numberingRangeId.trim() && !RANGE_ID.test(form.numberingRangeId.trim())) {
      found.numberingRangeId = 'Debe ser un número entero.';
    }
    if (form.prefix.trim() && !PREFIX.test(form.prefix.trim())) {
      found.prefix = 'Alfanumérico, de 1 a 10 caracteres.';
    }
    setErrors(found);
    if (Object.keys(found).length > 0) {return;}
    const input: UpsertPayrollElectronicInput = {
      environment: form.environment,
      numberingRangeId: form.numberingRangeId.trim() || null,
      prefix: form.prefix.trim() || null,
    };
    if (input.environment === 'PRODUCTION' && payroll?.environment !== 'PRODUCTION') {
      setConfirm(input);
      return;
    }
    mutation.mutate(input);
  }

  const error = (key: keyof PayrollForm): ReactNode =>
    errors[key] ? (
      <span className={styles.fieldError} role="alert">
        {errors[key]}
      </span>
    ) : null;

  return (
    <ConfigSection
      title="Nómina electrónica"
      description="Usa las mismas credenciales de Factus; el ambiente debe coincidir para activarla."
    >
      {payroll ? (
        <div className={factusStyles.facts}>
          <StatusChip status={payroll.status} />
          <EnvironmentChip environment={payroll.environment} />
        </div>
      ) : (
        <p className={styles.fieldHint}>Aún sin configurar (quedará en estado Pendiente).</p>
      )}
      <form onSubmit={handleSubmit} noValidate aria-label="Nómina electrónica" className={styles.panel}>
        <div className={styles.grid}>
          <label className={styles.field}>
            Ambiente de nómina
            <Select
              id={`${idPrefix}-environment`}
              value={form.environment}
              onChange={(value) => update('environment', value)}
              options={(Object.keys(ENVIRONMENT_LABEL) as FactusEnvironment[]).map((value) => ({
                value,
                label: ENVIRONMENT_LABEL[value],
              }))}
            />
          </label>
          <label className={styles.field}>
            Rango de numeración de nómina
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              id={`${idPrefix}-numberingRangeId`}
              aria-invalid={Boolean(errors.numberingRangeId)}
              value={form.numberingRangeId}
              onChange={(e) => update('numberingRangeId', e.target.value)}
            />
            {error('numberingRangeId')}
          </label>
          <label className={styles.field}>
            Prefijo
            <input
              type="text"
              autoComplete="off"
              id={`${idPrefix}-prefix`}
              aria-invalid={Boolean(errors.prefix)}
              value={form.prefix}
              placeholder="NE"
              onChange={(e) => update('prefix', e.target.value)}
            />
            {error('prefix')}
          </label>
        </div>
        {form.environment === 'PRODUCTION' ? (
          <p className={factusStyles.warning} role="note">
            {FACTUS_PRODUCTION_WARNING}
          </p>
        ) : null}
        {mutation.error && !(mutation.error instanceof ApiError && mutation.error.field) ? (
          <p className={styles.error} role="alert">
            {mutation.error.message}
          </p>
        ) : null}
        <div>
          <button type="submit" className={styles.saveBtn} disabled={mutation.isPending || !dirty}>
            <Save size={16} aria-hidden="true" />
            {mutation.isPending ? 'Guardando…' : 'Guardar nómina electrónica'}
          </button>
        </div>
        <p className={styles.success} role="status">
          {saved ? 'Nómina electrónica guardada.' : ''}
        </p>
      </form>
      {payroll ? statusButtons('payroll', payroll.status, dirty) : null}
      {confirm ? (
        <ConfirmDialog
          title="Nómina electrónica en producción"
          confirmLabel="Guardar en producción"
          danger
          pending={mutation.isPending}
          error={mutation.error ? mutation.error.message : null}
          onConfirm={() => mutation.mutate(confirm)}
          onCancel={() => {
            mutation.reset();
            setConfirm(null);
          }}
        >
          <p>{FACTUS_PRODUCTION_WARNING}</p>
          <p>La nómina electrónica volverá a Pendiente hasta que la actives de nuevo.</p>
        </ConfirmDialog>
      ) : null}
    </ConfigSection>
  );
}
